import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowRight,
  Camera,
  ChevronRight,
  Home,
  Ticket,
  Trophy,
  Gift,
  Paintbrush,
  Beaker,
  Candy,
  PartyPopper,
  Users,
  Sparkles,
  Album,
  ShieldCheck,
  Gamepad2,
} from 'lucide-react'
import { listSessions, getConfig, updateConfig, getGamePlaysCount } from '../utils/api.js'
import './Welcome.css'

const MOMENT_BUTTONS = [
  {
    icon: Sparkles,
    label: 'Egg Hunt',
  },
  {
    icon: Users,
    label: 'Family Photos',
  },
  {
    icon: PartyPopper,
    label: 'Kids Adventure',
  },
]

const FEATURED_CARDS = [
  {
    icon: Album,
    title: 'Shareable Memories',
    description: 'Easy access to every family gallery after the event. Share your favorite moments with friends and family through private, secure links that last forever.',
  },
  {
    icon: ShieldCheck,
    title: 'Security & Privacy',
    description: 'Private, family-safe handling of event details and photo memories. Your data stays protected with enterprise-grade security and automatic expiration.',
  },
]

const SPOTS = [
  { top: '8%', left: '6%', delay: '0s', size: '2.8rem', emoji: '🥚' },
  { top: '16%', right: '8%', delay: '1.2s', size: '2.2rem', emoji: '🐰' },
  { top: '68%', left: '8%', delay: '0.6s', size: '2.4rem', emoji: '🌸' },
  { top: '74%', right: '10%', delay: '1.6s', size: '2.6rem', emoji: '✨' },
  { top: '38%', left: '46%', delay: '0.9s', size: '2rem', emoji: '🐣' },
]

export default function Welcome() {
  const navigate = useNavigate()
  const [selectedMoment, setSelectedMoment] = useState('Egg Hunt')
  const [selectedHighlight, setSelectedHighlight] = useState(null)
  const [stats, setStats] = useState({ sessions: 0, photos: 0, games: 0 })
  const [churchName, setChurchName] = useState('')

  const ACTIVITY_CARDS = useMemo(() => [
    {
      icon: Home,
      title: 'Bounce House',
      description: 'Jump, laugh, and burn energy with nonstop fun.',
    },
    {
      icon: Ticket,
      title: 'Raffles',
      description: 'Enter to win surprise prizes throughout the celebration.',
    },
    {
      icon: Trophy,
      title: 'Prizes',
      description: 'Celebrate the winners with exciting Easter rewards.',
    },
    {
      icon: Gift,
      title: 'Gifts',
      description: 'Take-home goodies that keep the joy going after the event.',
    },
    {
      icon: Paintbrush,
      title: 'Crafts',
      description: 'Hands-on creative stations for kids and families.',
    },
    {
      icon: Beaker,
      title: 'Slime Stations',
      description: 'Messy, magical, and crowd-pleasing sensory fun.',
    },
    {
      icon: Camera,
      title: 'Photo Booth',
      description: `Capture and share joyful ${churchName} memories instantly.`,
    },
    {
      icon: Candy,
      title: 'Candy',
      description: 'Sweet treats to keep every smile extra bright.',
    },
  ], [churchName])

  useEffect(() => {
    loadData()
  }, [])

  async function loadData() {
    try {
      const [sessionsData, configData, gamePlays] = await Promise.all([
        listSessions(),
        getConfig(),
        getGamePlaysCount(),
      ])

      const safeSessions = Array.isArray(sessionsData) ? sessionsData : []
      const photoCount = safeSessions.reduce((sum, session) => sum + (session.photos?.length || 0), 0)
      setStats({ sessions: safeSessions.length, photos: photoCount, games: gamePlays })

      setChurchName(configData?.church_name ?? '')
    } catch (err) {
      console.error('Failed to load welcome data:', err)
      setStats({ sessions: 0, photos: 0, games: 0 })
    }
  }

  async function startBooth() {
    try {
      await updateConfig({
        gallery_base_url: window.location.origin,
      })
    } catch (err) {
      console.error('Failed to save event config:', err)
    } finally {
      navigate('/booth')
    }
  }

  const STAT_FACTS = [
    { icon: Users, value: stats.sessions.toLocaleString(), label: 'sessions captured' },
    { icon: Gamepad2, value: stats.games.toLocaleString(), label: 'games played' },
    { icon: Camera, value: stats.photos.toLocaleString(), label: 'photos captured' },
  ]

  return (
    <main className="welcome-page">
      <div className="welcome-backdrop" aria-hidden="true">
        <div className="sunrise-glow" />
        <div className="welcome-orb orb-one" />
        <div className="welcome-orb orb-two" />
        <div className="welcome-orb orb-three" />
        {SPOTS.map((spot, index) => (
          <div
            key={`${spot.emoji}-${index}`}
            className="floating-egg"
            style={{
              top: spot.top,
              left: spot.left,
              right: spot.right,
              animationDelay: spot.delay,
              fontSize: spot.size,
            }}
            aria-hidden="true"
          >
            <span>{spot.emoji}</span>
          </div>
        ))}
      </div>

      <section className="welcome-shell">
        <div className="welcome-grid">
          <div className="welcome-copy animate-fade-in">
            <div className="brand-lockup">
              <img
                src="/logo.png"
                alt={`${churchName} logo`}
                className="brand-logo"
                loading="eager"
              />
            </div>

            <div className="eyebrow-pill">
              <Sparkles size={16} />
              <span>Interactive Easter celebration experience</span>
            </div>

            <h1 className="welcome-title">{churchName}'s Easter Celebration</h1>

            <div className="fact-row">
              {STAT_FACTS.map(fact => {
                const FactIcon = fact.icon

                return (
                  <div key={fact.label} className="fact-card">
                    <span className="fact-card-icon" aria-hidden="true">
                      <FactIcon size={18} />
                    </span>
                    <div className="fact-card-copy">
                      <strong>{fact.value}</strong>
                      <span>{fact.label}</span>
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="hero-primary-actions">
              <button className="hero-primary-btn hero-primary-btn--easter" onClick={startBooth}>
                <Camera size={20} />
                Start Photo Booth
                <ArrowRight size={18} />
              </button>
            </div>

            <div className="hero-secondary-actions">
              <button className="hero-secondary-btn" onClick={() => navigate('/admin')}>
                Admin
              </button>
              <button className="hero-secondary-btn" onClick={() => navigate('/resources')}>
                Explore Resources
              </button>
            </div>
          </div>

          <div className="welcome-stage animate-slide-up">
            <div className="moment-float-row" aria-label="Easter moment shortcuts">
              {MOMENT_BUTTONS.map(moment => {
                const MomentIcon = moment.icon

                return (
                  <button
                    key={moment.label}
                    type="button"
                    className={selectedMoment === moment.label ? 'moment-chip active' : 'moment-chip'}
                    onClick={() => setSelectedMoment(moment.label)}
                    aria-pressed={selectedMoment === moment.label}
                  >
                    <span className="moment-chip-icon" aria-hidden="true">
                      <MomentIcon size={16} />
                    </span>
                    <span>{moment.label}</span>
                  </button>
                )
              })}
            </div>

            <div className="highlight-grid">
              {ACTIVITY_CARDS.map(item => {
                const Icon = item.icon

                return (
                  <button
                    key={item.title}
                    type="button"
                    className={selectedHighlight === item.title ? 'highlight-card active' : 'highlight-card'}
                    onClick={() => setSelectedHighlight(prev => (prev === item.title ? null : item.title))}
                    aria-pressed={selectedHighlight === item.title}
                  >
                    <div className="highlight-icon">
                      <Icon size={18} />
                    </div>
                    <span className="highlight-card-label">{item.title}</span>
                  </button>
                )
              })}
            </div>

            <div className="cta-strip">
              <div>
                <p className="cta-title">Bring the Easter story to life</p>
                <p className="cta-copy">
                  Experience the interactive Easter story through a full-screen journey designed to
                  feel immersive, joyful, and ready for every family.
                </p>
              </div>
              <button className="cta-link" onClick={() => navigate('/experience')}>
                <span>Begin the experience</span>
                <ChevronRight size={18} />
              </button>
            </div>

            <div className="featured-grid">
              {FEATURED_CARDS.map(item => {
                const Icon = item.icon

                return (
                  <div key={item.title} className="featured-card">
                    <div className="featured-icon">
                      <Icon size={24} />
                    </div>
                    <div className="featured-content">
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}
